import { useMemo } from "react"
import { WHATS_NEW_LINK } from "@/v2-common/featureSurface/featureSurface.constants"
import cn from "@/v2-ui/utils/utils.cn"
import Separator from "@/v2-ui/separator"
import { useAppViewSheetOpener } from "@/v2-backoffice-client/app/app.utils"
import Table, { type TableProps } from "@/v2-backoffice-client/components/table"
import LicenseContentAbout from "@/v2-backoffice-client/license/changes/LicenseContentAbout"
import LicenseContentRowToolbar from "@/v2-backoffice-client/license/changes/LicenseContentRowToolbar"
import { MAP_CONTENT_SHEET_KIND } from "@/v2-backoffice-client/license/changes/license.changes.constants"
import { LicenseChangesContext } from "@/v2-backoffice-client/license/changes/license.changes.context"
import { createContentColumns } from "@/v2-backoffice-client/license/changes/license.content.columns"
import {
  contentStatusOf,
  CONTENT_STATUS,
  type LicenseContentItem,
} from "@/v2-backoffice-client/license/changes/license.content.utils"
import { useFeatureContentCatalogQuery } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.contentCatalog.query"
import { useFeatureGroupsQuery } from "@/v2-backoffice-client/license/featureGroup/license.featureGroup.query"

export type LicenseContentTableProps = Omit<
  TableProps<LicenseContentItem>,
  "data" | "columns"
>

// The Content view of Feature access: every base map and map layer of the
// content catalog, with whether anyone can see it yet, what has been
// decided and which packages carry it. The row opens the item's panel,
// where the decision is taken with the lock's blast radius in view, so a
// map layer is decided where a capability is and nowhere else (the layer
// sheet used to have a select of its own, FUS-2787). The board owns the
// two reads (the content catalog, shared through the Apollo cache with the
// package editor's Content view and the panel, and the package vocabulary
// the Packages pills link by).
function LicenseContentTable(props: LicenseContentTableProps) {
  const { className, ...tableProps } = props
  const opener = useAppViewSheetOpener()

  const { data, previousData, loading, error, refetch } =
    useFeatureContentCatalogQuery()
  const { data: packagesData } = useFeatureGroupsQuery()
  // The lensed hook hands the rows out read-only; the table wants its own.
  // previousData keeps rows rendered through post-save evictions.
  const catalog = (data ?? previousData)?.getFeatureContentCatalog
  const items = useMemo(() => [...(catalog ?? [])], [catalog])
  const packages = packagesData?.getFeatureGroups ?? []

  const counts = useMemo(() => {
    const counted = {
      [CONTENT_STATUS.packageOnly]: 0,
      [CONTENT_STATUS.everyone]: 0,
      [CONTENT_STATUS.notLive]: 0,
    }
    for (const item of items) counted[contentStatusOf(item)] += 1
    return counted
  }, [items])

  const columns = useMemo(() => createContentColumns(), [])

  return (
    <LicenseChangesContext value={{ packages }}>
      <div className="flex h-full min-h-0 flex-col gap-2">
        <LicenseContentAbout />
        <Table
          className={cn("license-content-table min-h-0 flex-1", className)}
          tableId="license-feature-access-content"
          data={items}
          columns={columns}
          enableGrouping
          getRowId={(row) => row.Code}
          onRowClick={(row) => {
            opener.open(MAP_CONTENT_SHEET_KIND, row.original.Code)
          }}
          getHighlightedRowId={(row) =>
            opener.getIsActive(MAP_CONTENT_SHEET_KIND, row.original.Code)
          }
          {...tableProps}
        >
          <Table.Toolbar>
            <Table.StatusFilter
              column="Status"
              urlParam={WHATS_NEW_LINK.statusParam}
              isLoading={loading}
            >
              <Table.StatusFilter.Option
                value={0}
                filterValue={CONTENT_STATUS.packageOnly}
                iconSrc="lock"
                count={counts[CONTENT_STATUS.packageOnly]}
              >
                Package-only
              </Table.StatusFilter.Option>
              <Table.StatusFilter.Option
                value={1}
                filterValue={CONTENT_STATUS.everyone}
                variant="success"
                iconSrc="unlock"
                count={counts[CONTENT_STATUS.everyone]}
              >
                Free for everyone
              </Table.StatusFilter.Option>
              <Table.StatusFilter.Option
                value={2}
                filterValue={CONTENT_STATUS.notLive}
                iconSrc="eye-crossed"
                count={counts[CONTENT_STATUS.notLive]}
              >
                Not live
              </Table.StatusFilter.Option>
              <Table.StatusFilter.Option
                value={null}
                filterValue={WHATS_NEW_LINK.statusAll}
                iconSrc="list"
                count={items.length}
              >
                All
              </Table.StatusFilter.Option>
            </Table.StatusFilter>

            <div className="flex-1" />
            {/* Deep-linkable (a layer's or base map's sheet opens its row
                here by code), so the keys are the shared WHATS_NEW_LINK. */}
            <Table.Search
              column="Content"
              urlParam={WHATS_NEW_LINK.searchParam}
              placeholder="Search by layer or base map..."
            />

            <Table.ToolbarFilters />
            <Separator orientation="vertical" />

            <Table.ToolbarMenu
              onRefresh={() => void refetch()}
              exportFileName="feature-access-content"
            />
          </Table.Toolbar>

          <Table.FilterList />
          <Table.GroupList />

          <Table.RowToolbar<LicenseContentItem> column="Content">
            {(item) => <LicenseContentRowToolbar item={item} />}
          </Table.RowToolbar>

          <Table.Content isLoading={loading} error={error} />
        </Table>
      </div>
    </LicenseChangesContext>
  )
}

export default LicenseContentTable
